import { useMutation } from '@tanstack/react-query';
import { startAuthentication } from '@simplewebauthn/browser';
import { SKIP_FORBIDDEN_HEADER, SKIP_UNAUTH_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

/**
 * The MFA challenge is an unauthenticated flow that renders its own errors, so
 * every call here skips both global handlers. A 403 from these endpoints is an
 * IP-policy or risk block, which the verify screen already explains.
 */
const CHALLENGE_HEADERS = {
  [SKIP_UNAUTH_HEADER]: '1',
  [SKIP_FORBIDDEN_HEADER]: '1',
} as const;

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface ChallengeError extends Error {
  status?: number;
  body?: ErrorBody;
}

export interface MfaChallengeResult {
  access_token?: string;
  refresh_token?: string;
  family_id?: string;
  token_type?: string;
  expires_in?: number;
  /** Recovery flow may include freshly regenerated codes when stock is low. */
  new_recovery_codes?: string[];
}

function toError(stage: string, status: number | undefined, error: unknown): ChallengeError {
  const e = new Error(stage) as ChallengeError;
  if (status !== undefined) e.status = status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

/**
 * Verify a TOTP code carrying an `mfa_token` issued by the login flow.
 * The server sets HttpOnly auth cookies and also returns tokens in the JSON
 * body (for clients that don't use cookies). Callers only need the success
 * signal to navigate; cookies do the heavy lifting.
 */
export function useTotpVerify(mfaToken: string | null) {
  const api = useApi();
  return useMutation<MfaChallengeResult, ChallengeError, { code: string }>({
    mutationFn: async ({ code }) => {
      if (!mfaToken) throw toError('mfa_token_missing', 401, { error: 'invalid_token' });
      const { data, error, response } = await api.POST('/auth/mfa/verify', {
        body: { mfa_token: mfaToken, code },
        headers: CHALLENGE_HEADERS,
      });
      if (error) throw toError('mfa_verify_failed', response?.status, error);
      return (data ?? {}) as MfaChallengeResult;
    },
  });
}

/**
 * Verify a recovery code carrying an `mfa_token`. Recovery codes are
 * alphanumeric, single-use; the server may include `new_recovery_codes` if
 * the user is running low.
 */
export function useRecoveryVerify(mfaToken: string | null) {
  const api = useApi();
  return useMutation<MfaChallengeResult, ChallengeError, { recovery_code: string }>({
    mutationFn: async ({ recovery_code }) => {
      if (!mfaToken) throw toError('mfa_token_missing', 401, { error: 'invalid_token' });
      const { data, error, response } = await api.POST('/auth/mfa/recovery', {
        body: { mfa_token: mfaToken, recovery_code },
        headers: CHALLENGE_HEADERS,
      });
      if (error) throw toError('mfa_recovery_failed', response?.status, error);
      return (data ?? {}) as MfaChallengeResult;
    },
  });
}

/**
 * WebAuthn second-factor verify ceremony. Orchestrates the start → browser
 * ceremony → finish handshake. Exposed as a single mutation; the caller does
 * not need to manage challenge_id state.
 */
export function useWebauthnVerify(mfaToken: string | null) {
  const api = useApi();
  return useMutation<MfaChallengeResult, ChallengeError, void>({
    mutationFn: async () => {
      if (!mfaToken) throw toError('mfa_token_missing', 401, { error: 'invalid_token' });

      const startRes = await api.POST('/auth/mfa/webauthn/start', {
        body: { mfa_token: mfaToken },
        headers: CHALLENGE_HEADERS,
      });
      if (startRes.error) {
        throw toError('mfa_webauthn_start_failed', startRes.response?.status, startRes.error);
      }
      const startData = (startRes.data ?? {}) as {
        challenge_id?: string;
        options?: unknown;
      };
      if (!startData.challenge_id || !startData.options) {
        throw toError('mfa_webauthn_start_failed', 500, { error: 'invalid_start_response' });
      }

      // The server returns the assertion options in webauthn-rs's wrapped
      // shape (`{ publicKey: { ... } }`). Unwrap if present so
      // startAuthentication gets just the inner options.
      const optsRaw = startData.options as { publicKey?: unknown } | unknown;
      const optionsJSON = (
        optsRaw && typeof optsRaw === 'object' && 'publicKey' in (optsRaw as object)
          ? (optsRaw as { publicKey: unknown }).publicKey
          : optsRaw
      ) as Parameters<typeof startAuthentication>[0]['optionsJSON'];

      const credential = await startAuthentication({ optionsJSON });

      const finishRes = await api.POST('/auth/mfa/webauthn/finish', {
        body: {
          mfa_token: mfaToken,
          challenge_id: startData.challenge_id,
          credential,
        },
        headers: CHALLENGE_HEADERS,
      });
      if (finishRes.error) {
        throw toError('mfa_webauthn_finish_failed', finishRes.response?.status, finishRes.error);
      }
      return (finishRes.data ?? {}) as MfaChallengeResult;
    },
  });
}
